<template>
  <div class="dropdown">
    <div
      class="dropdown__selected"
      @click="toggleDropdown">
      <span class="dropdown__selected-left">
        <svg class="dropdown__icon">
          <use :xlink:href="`#${discount}`" />
        </svg>
        <span>
          {{
            selectedOption ? selectedOption.label : placeholder || $t('components.dropdown.select')
          }}
        </span>
      </span>
      <span
        class="dropdown__chevron"
        :class="{
          'dropdown__chevron--selected': selectedOption,
          'dropdown__chevron--open': isOpen,
        }">
        <svg>
          <use :xlink:href="`#${chevron}`" />
        </svg>
      </span>
    </div>
    <ul
      v-if="isOpen"
      class="dropdown__options">
      <li
        v-for="option in options"
        :key="option.value"
        class="dropdown__option"
        :class="{
          'dropdown__option--selected': option.value === selectedOption?.value,
        }"
        @click.stop="selectOption(option)">
        {{ option.label }}
      </li>
      <li
        v-if="options.length === 0"
        class="dropdown__option dropdown__option--no-results">
        {{ $t('components.dropdown.noResults') }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref } from 'vue'
  import chevron from '@/assets/icons/chevron.svg'
  import discount from '@/assets/icons/discount.svg'

  export default defineComponent({
    name: 'Dropdown',
    props: {
      options: {
        type: Array as () => Array<{ label: string; value: string }>,
        required: true,
      },
      placeholder: {
        type: String,
        default: 'Select an option',
      },
      icon: {
        type: String,
        default: '',
      },
    },
    setup(props, { emit }) {
      const isOpen = ref(false)
      const selectedOption = ref<{ label: string; value: string } | null>(null)

      const toggleDropdown = () => {
        isOpen.value = !isOpen.value
      }

      const selectOption = (option: { label: string; value: string }) => {
        selectedOption.value = option
        isOpen.value = false
        emit('update:selected', option.value)
      }

      return {
        isOpen,
        selectedOption,
        toggleDropdown,
        selectOption,
        chevron,
        discount,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .dropdown {
    width: 100%;
    position: relative;
    cursor: pointer;
    user-select: none;
    margin-bottom: 0.8rem;
    touch-action: manipulation;

    &__selected {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      padding: 0.5rem 1rem;
      border: 1px solid $clr-primary-100;
      border-radius: 8px;
      background-color: $clr-white;
      box-shadow: $box-shadow-card;
      font-size: 16px;

      &-left {
        display: flex;
        align-items: center;
      }
    }

    &__icon {
      margin: 0 0.5rem 0 0;
      color: $clr-primary-200;
      z-index: 10;
      width: 16px;
      height: 16px;
    }

    &__chevron {
      transition: transform 0.2s ease;

      svg {
        color: $clr-primary-200;
        width: 1rem;
        height: 1rem;
      }

      &--open {
        transform: rotate(180deg);
      }

      &--selected {
        transform: rotate(90deg);
      }
    }

    &__options {
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      border: 1px solid $clr-primary-100;
      border-radius: 8px;
      background-color: $clr-white;
      box-shadow: $box-shadow-card;
      z-index: 1000;
      max-height: 200px;
      overflow-y: auto;
      list-style: none;
      padding: 0;
    }

    &__option {
      padding: 0.625rem 0 0.625rem 2.5rem;
      cursor: pointer;

      &:hover {
        background-color: $clr-primary-50;
      }

      &--selected {
        background-color: $clr-primary-50;
      }

      &--no-results {
        padding: 0.625rem 2.5rem;
        text-align: center;
        color: $clr-secondary;
      }
    }
  }
</style>
